import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProgramNode } from '../../../common/models/care-monitor/program-node';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { ProgramInput, ProgramLog } from '@meplis/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'mec-program-list-input',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './program-list-input.component.html',
  styleUrls: ['./program-list-input.component.scss'],
})
export class ProgramListInputComponent implements OnDestroy, OnChanges {
  @Input() public programNode: ProgramNode;
  @Output() public onSelectItemChange = new EventEmitter<{
    programLog: ProgramLog;
    valid: boolean;
  }>();

  public formArray: FormArray = new FormArray([]);
  private subscriptions: Subscription[] = [];

  public inputKey: ProgramInput;
  public inputValue: ProgramInput;

  constructor() {}

  ngOnDestroy(): void {
    this._clearSubscriptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.programNode || !this.programNode.inputs) {
      return;
    }

    this._clearSubscriptions();
    this.formArray.clear();

    this.inputKey = this.programNode.inputs[0];
    this.inputValue = this.programNode.inputs[1];

    const results = this.programNode?.log?.results ?? [];
    const grouped: { key?: string; value?: string }[] = [];

    if (results && results.length) {
      const byName: Record<string, any[]> = {};
      results.forEach((r) => {
        if (!byName[r.name]) byName[r.name] = [];
        byName[r.name].push(r.value);
      });

      const keyArr = byName[this.inputKey?.name] ?? [];
      const valueArr = byName[this.inputValue?.name] ?? [];

      const maxLen = Math.max(keyArr.length, valueArr.length);
      for (let i = 0; i < Math.max(1, maxLen); i++) {
        grouped.push({
          key: keyArr[i] !== undefined ? String(keyArr[i]) : '',
          value: valueArr[i] !== undefined ? String(valueArr[i]) : '',
        });
      }
    }

    const initialValues =
      grouped.length > 0
        ? grouped
        : [
            {
              key: '',
              value: '',
            },
          ];

    const valuesToCreate = this.programNode.multiple
      ? initialValues
      : [initialValues[0] ?? { key: '', value: '' }];

    valuesToCreate.forEach((val) =>
      this._createGroup(String(val.key ?? ''), String(val.value ?? ''))
    );

    this._emitProgramLog();
  }

  public getFormControl(control: AbstractControl): FormControl {
    return control as FormControl;
  }

  public addMore(): void {
    this._createGroup('', '');
    this._emitProgramLog();
  }

  public remove(index: number): void {
    const sub = this.subscriptions[index];
    if (sub) {
      sub.unsubscribe();
    }
    this.subscriptions.splice(index, 1);
    this.formArray.removeAt(index);
    this._emitProgramLog();
  }

  private _createGroup(keyValue: string, valueValue: string): void {
    const keyValidators = this._buildValidatorsForInput(this.inputKey);
    const valueValidators = this._buildValidatorsForInput(this.inputValue);

    const group = new FormGroup({
      key: new FormControl(keyValue ?? '', keyValidators),
      value: new FormControl(valueValue ?? '', valueValidators),
    });

    this.formArray.push(group);

    const sub = group.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this._emitProgramLog();
      });

    this.subscriptions.push(sub);
  }

  private _buildValidatorsForInput(input?: ProgramInput): any[] {
    const v: any[] = [];
    if (!input) return v;

    if (input.required || input.required === true) {
      v.push(Validators.required);
    }

    if (input.validation) {
      if ((input.validation as any).minLength) {
        v.push(Validators.minLength((input.validation as any).minLength));
      }
      if ((input.validation as any).maxLength) {
        v.push(Validators.maxLength((input.validation as any).maxLength));
      }
    }

    if (input.type === 'number') {
      if (input.minValue !== undefined && input.minValue !== null) {
        v.push(Validators.min(input.minValue));
      }
      if (input.maxValue !== undefined && input.maxValue !== null) {
        v.push(Validators.max(input.maxValue));
      }
    }

    return v;
  }

  private _emitProgramLog(): void {
    const results: any[] = [
      {
        name: this.inputKey?.name,
        value: [] as (string | number)[],
      },
      {
        name: this.inputValue?.name,
        value: [] as (string | number)[],
      },
    ];

    this.formArray.controls.forEach((grp: AbstractControl) => {
      const g = grp as FormGroup;
      const k = g.get('key')?.value;
      const val = g.get('value')?.value;

      const resultInputs: { name: string; value: string | number }[] = [];

      if (k != null && k !== '') {
        resultInputs.push({ name: this.inputKey?.name, value: k });
        results[0].value.push(k);
      }
      if (val != null && val !== '') {
        resultInputs.push({ name: this.inputValue?.name, value: val });
        results[1].value.push(val);
      }

      // if (resultInputs.length > 0) {
      //   results.push(resultInputs);
      // }
    });

    const log: ProgramLog = {
      to: this.programNode.id,
      at: new Date().toISOString(),
      results,
    };

    const valid = this.formArray.valid;
    this.onSelectItemChange.emit({ programLog: log, valid });
  }

  private _clearSubscriptions(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
  }
}
